if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        let cartRequestToken = null;
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof this.cart.beginAdd === 'function') {
            cartRequestToken = this.cart.beginAdd(this.submitButton);
          } else {
            this.cart.setActiveElement(this.submitButton);
          }
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        const executeAddRequest = () => {
          const addRequest = () => fetch(`${routes.cart_add_url}`, config);
          const addResponse = this.cart?.queueCartRequest ? this.cart.queueCartRequest(addRequest) : addRequest();

          addResponse
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.cart?.showError?.(response.description || response.message, cartRequestToken);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response, cartRequestToken);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response, cartRequestToken);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.cart?.showError?.(e.message || 'Unable to add this item. Please try again.', cartRequestToken);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.cart?.finishAdd?.(cartRequestToken);
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
        };

        // Enforce House of Banic cart limits on the frontend before submitting
        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart || !cart.items) return executeAddRequest();

            const existingItem = cart.items.find(item => item.variant_id == variantId);
            let itemSize = null;
            
            if (existingItem) {
              if (existingItem.options_with_values) {
                existingItem.options_with_values.forEach((opt) => {
                  const val = (opt.value || '').replace(/\s/g, '').toLowerCase();
                  if (val === '10ml' || val === '50ml') itemSize = val;
                });
              }
            } else {
              const inputs = this.form.querySelectorAll('input:checked, select option:checked');
              inputs.forEach(el => {
                const val = ((el.value || '') + (el.textContent || '')).replace(/\s/g, '').toLowerCase();
                if (val.includes('10ml')) itemSize = '10ml';
                if (val.includes('50ml')) itemSize = '50ml';
              });
              
              if (!itemSize) {
                const formText = this.form.textContent.replace(/\s/g, '').toLowerCase();
                const productTitle = this.closest('.card, .product__info-container')?.textContent.replace(/\s/g, '').toLowerCase() || '';
                if (formText.includes('10ml') || productTitle.includes('10ml')) itemSize = '10ml';
                else if (formText.includes('50ml') || productTitle.includes('50ml')) itemSize = '50ml';
              }
            }
            
            let tenMlQty = 0;
            let fiftyMlQty = 0;
            
            cart.items.forEach((cartItem) => {
              let size = '';
              if (cartItem.options_with_values) {
                cartItem.options_with_values.forEach((opt) => {
                  const val = (opt.value || '').replace(/\s/g, '').toLowerCase();
                  if (val === '10ml' || val === '50ml') size = val;
                });
              }
              if (size === '10ml') tenMlQty += cartItem.quantity;
              if (size === '50ml') fiftyMlQty += cartItem.quantity;
            });

            if (itemSize === '10ml' && tenMlQty + quantity > 8) {
              this.handleErrorMessage('You can include up to 8 × 10 ml fragrances in one order.');
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButton.classList.remove('loading');
              this.querySelector('.loading__spinner').classList.add('hidden');
              this.cart?.finishAdd?.(cartRequestToken);
              return;
            }
            if (itemSize === '50ml' && fiftyMlQty + quantity > 8) {
              this.handleErrorMessage('You can include up to 8 × 50 ml fragrances in one order.');
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButton.classList.remove('loading');
              this.querySelector('.loading__spinner').classList.add('hidden');
              this.cart?.finishAdd?.(cartRequestToken);
              return;
            }
            if ((itemSize === '10ml' || itemSize === '50ml' || tenMlQty > 0 || fiftyMlQty > 0) && (tenMlQty + fiftyMlQty + quantity > 16)) {
              this.handleErrorMessage('Your fragrance edit can include up to 16 bottles per order.');
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButton.classList.remove('loading');
              this.querySelector('.loading__spinner').classList.add('hidden');
              this.cart?.finishAdd?.(cartRequestToken);
              return;
            }

            executeAddRequest();
          })
          .catch(() => {
            executeAddRequest();
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
